import { Layout } from "@/components/layout/layout";
import { useParams, Link } from "wouter";
import {
  useGetTournament,
  getGetTournamentQueryKey,
  useListMyRegistrations,
  useCancelMyRegistration,
  useCreateTournamentRegistration,
  useCreateStripeCheckoutSession,
  getListMyRegistrationsQueryKey,
  useGetBlindStructure,
  getGetBlindStructureQueryKey,
  useGetPayoutStructure,
  getGetPayoutStructureQueryKey,
  useListTournamentRegistrations,
  getListTournamentRegistrationsQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { formatMoney, formatDateTime } from "@/lib/format";
import {
  Calendar,
  MapPin,
  Users,
  Trophy,
  Loader2,
  ArrowLeft,
  X,
  Clock,
  Layers,
  ChevronDown,
  EyeOff,
  CalendarPlus,
} from "lucide-react";
import { storedImageUrl } from "@/lib/upload";
import { useCurrentPlayer } from "@/hooks/useCurrentPlayer";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { useEffect, useMemo, useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

function formatCountdown(ms: number): string {
  if (ms <= 0) return "0s";
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatStartCountdown(ms: number): string {
  if (ms <= 0) return "Starting now";
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const h = Math.floor((totalSeconds % 86400) / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (days > 0) return `${days}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatBlinds(level: { smallBlind: number; bigBlind: number; ante: number }): string {
  const base = `${level.smallBlind} / ${level.bigBlind}`;
  return level.ante > 0 ? `${base} (ante ${level.ante})` : base;
}

export default function TournamentDetail() {
  const { id } = useParams<{ id: string }>();
  const tournamentId = Number(id);
  const { player } = useCurrentPlayer();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: tournament, isLoading } = useGetTournament(tournamentId, {
    query: { enabled: !!tournamentId, queryKey: getGetTournamentQueryKey(tournamentId) }
  });

  const myRegistrationsKey = getListMyRegistrationsQueryKey();
  const { data: myRegistrations } = useListMyRegistrations({
    query: { enabled: !!player, queryKey: myRegistrationsKey },
  });
  const myReg = myRegistrations?.find((r) => r.tournamentId === tournamentId);
  const isRegistered = !!myReg;
  const myRegStatus = myReg?.registrationStatus;
  const myWaitlistPos = myReg?.waitlistPosition ?? null;
  const capacity = tournament?.capacity ?? null;
  const registeredCount = tournament?.registeredCount ?? null;
  const lateRegEndsAt = tournament?.lateRegEndsAt ?? null;
  const tournamentStart = tournament ? new Date(tournament.date).getTime() : 0;
  const now = Date.now();
  const lateEnd = lateRegEndsAt ? new Date(lateRegEndsAt).getTime() : null;
  const inLateWindow = !!lateEnd && now >= tournamentStart && now <= lateEnd;
  const willJoinWaitlist = !isRegistered && capacity != null && registeredCount != null && registeredCount >= capacity;

  const [lateRemainingMs, setLateRemainingMs] = useState<number | null>(
    lateEnd ? lateEnd - Date.now() : null,
  );
  useEffect(() => {
    if (!lateEnd) { setLateRemainingMs(null); return; }
    setLateRemainingMs(lateEnd - Date.now());
    const t = setInterval(() => setLateRemainingMs(lateEnd - Date.now()), 1000);
    return () => clearInterval(t);
  }, [lateEnd]);
  const showLateCountdown = lateRemainingMs != null && lateRemainingMs > 0 && tournament?.status === "active" && lateEnd != null && Date.now() >= tournamentStart;

  // Live countdown to start
  const [startRemainingMs, setStartRemainingMs] = useState<number>(
    tournamentStart ? tournamentStart - Date.now() : 0,
  );
  useEffect(() => {
    if (!tournamentStart) return;
    setStartRemainingMs(tournamentStart - Date.now());
    const t = setInterval(() => setStartRemainingMs(tournamentStart - Date.now()), 1000);
    return () => clearInterval(t);
  }, [tournamentStart]);
  const showStartCountdown = !!tournament && tournament.status === "upcoming";

  const blindStructureId = tournament?.blindStructureId ?? null;
  const { data: blindStructure } = useGetBlindStructure(blindStructureId ?? 0, {
    query: {
      enabled: !!blindStructureId,
      queryKey: getGetBlindStructureQueryKey(blindStructureId ?? 0),
    },
  });

  const payoutStructureId = tournament?.payoutStructureId ?? null;
  const { data: payoutStructure } = useGetPayoutStructure(payoutStructureId ?? 0, {
    query: {
      enabled: !!payoutStructureId,
      queryKey: getGetPayoutStructureQueryKey(payoutStructureId ?? 0),
    },
  });

  const { data: roster, isLoading: isRosterLoading } = useListTournamentRegistrations(tournamentId, {
    query: {
      enabled: !!tournamentId,
      queryKey: getListTournamentRegistrationsQueryKey(tournamentId),
    },
  });

  const activeRoster = useMemo(
    () => (roster ?? []).filter((r) => r.registrationStatus === "registered" || r.registrationStatus === "late"),
    [roster],
  );
  const waitlistRoster = useMemo(
    () =>
      (roster ?? [])
        .filter((r) => r.registrationStatus === "waitlist")
        .slice()
        .sort((a, b) => (a.waitlistPosition ?? 999) - (b.waitlistPosition ?? 999)),
    [roster],
  );
  const anonymousActiveCount = useMemo(
    () => activeRoster.filter((r) => !r.isPublicProfile && r.id !== player?.id).length,
    [activeRoster, player?.id],
  );

  const estimatedPayouts = useMemo(() => {
    if (!tournament || !payoutStructure?.tiers?.length) return [];
    const pool = tournament.prizePool;
    return payoutStructure.tiers
      .slice()
      .sort((a, b) => a.position - b.position)
      .map((tier) => ({
        position: tier.position,
        points: tier.points,
        prizePercent: Number(tier.prizePercent),
        amount: pool * (Number(tier.prizePercent) / 100),
      }));
  }, [tournament, payoutStructure]);

  const invalidateAfterChange = () => {
    queryClient.invalidateQueries({ queryKey: getGetTournamentQueryKey(tournamentId) });
    queryClient.invalidateQueries({ queryKey: myRegistrationsKey });
    queryClient.invalidateQueries({ queryKey: getListTournamentRegistrationsQueryKey(tournamentId) });
  };

  const cancelMutation = useCancelMyRegistration({
    mutation: {
      onSuccess: () => {
        invalidateAfterChange();
        toast({ title: "RSVP cancelled", description: "Hope to see you next time." });
      },
      onError: (err: Error) => {
        let description = "Could not cancel your RSVP.";
        try {
          const parsed = JSON.parse(err.message) as { error?: string };
          if (parsed?.error) description = parsed.error;
        } catch {
          // not JSON
        }
        toast({ title: "Cancellation failed", description, variant: "destructive" });
      },
    },
  });

  const checkoutMutation = useCreateStripeCheckoutSession({
    mutation: {
      onSuccess: (data) => {
        if (data?.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        } else {
          toast({
            title: "Could not start checkout",
            description: "Please try again or pay at the desk.",
            variant: "destructive",
          });
        }
      },
      onError: (err: Error) => {
        let description = "Could not start card payment.";
        try {
          const parsed = JSON.parse(err.message) as { error?: string };
          if (parsed?.error) description = parsed.error;
        } catch { /* not JSON */ }
        toast({ title: "Checkout failed", description, variant: "destructive" });
      },
    },
  });

  // Show a toast when returning from Stripe Checkout via ?stripe=success|cancelled.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripeFlag = params.get("stripe");
    if (stripeFlag === "success") {
      toast({ title: "Payment received", description: "Your seat is confirmed." });
      invalidateAfterChange();
    } else if (stripeFlag === "cancelled") {
      toast({
        title: "Payment cancelled",
        description: "Your seat is held as pending — pay at the desk or try again.",
      });
    }
    if (stripeFlag) {
      params.delete("stripe");
      params.delete("session_id");
      const qs = params.toString();
      const url = window.location.pathname + (qs ? `?${qs}` : "");
      window.history.replaceState(null, "", url);
    }
    // Only run on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registerMutation = useCreateTournamentRegistration({
    mutation: {
      onSuccess: () => {
        invalidateAfterChange();
        toast({ title: "You're registered", description: "See you at the tables." });
      },
      onError: (err: Error) => {
        toast({
          title: "Registration failed",
          description: err.message.includes("already") ? "You're already on the list." : "Could not register.",
          variant: "destructive",
        });
      },
    },
  });

  if (isLoading) return <Layout><div className="flex-1 flex justify-center items-center"><Loader2 className="h-8 w-8 animate-spin" /></div></Layout>;
  if (!tournament) return <Layout><div className="flex-1 flex justify-center items-center">Tournament not found</div></Layout>;

  const heroUrl = storedImageUrl(tournament.imageUrl ?? null);

  return (
    <Layout>
      {heroUrl && (
        <div className="relative w-full h-64 sm:h-80 lg:h-96 overflow-hidden bg-card border-b border-border">
          <img src={heroUrl} alt={tournament.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/30 to-transparent" />
        </div>
      )}
      <div className="bg-card border-b border-border pt-8 pb-12">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link href="/tournaments" className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" /> Back to Schedule
          </Link>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Badge variant={tournament.status === 'upcoming' ? 'default' : 'secondary'} className="text-sm px-3 py-1">
                  {tournament.status.toUpperCase()}
                </Badge>
                <span className="text-primary font-bold">{tournament.leagueName}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight">{tournament.name}</h1>
              {showStartCountdown && (
                <div
                  className="mt-4 inline-flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-1.5 text-sm font-medium text-primary"
                  data-testid="badge-start-countdown"
                >
                  <Clock className="h-4 w-4" />
                  Starts in {formatStartCountdown(startRemainingMs)}
                </div>
              )}
            </div>

            {(tournament.status === "upcoming" || (tournament.status === "active" && inLateWindow) || isRegistered) && tournament.status !== "completed" && (
              <div className="w-full md:w-auto">
                {!player ? (
                  <Link href="/login">
                    <Button size="lg" className="w-full md:w-auto bg-primary text-primary-foreground font-bold px-8">
                      Sign in to RSVP
                    </Button>
                  </Link>
                ) : isRegistered ? (
                  <div className="flex flex-col items-end gap-2">
                    {myRegStatus === "waitlist" && (
                      <Badge variant="outline" className="border-amber-500/40 text-amber-500" data-testid="badge-waitlist">
                        You&apos;re #{myWaitlistPos ?? "?"} on the waitlist
                      </Badge>
                    )}
                    {myRegStatus === "late" && (
                      <Badge variant="outline" className="border-blue-500/40 text-blue-500">Late registration</Badge>
                    )}
                    {showLateCountdown && (
                      <Badge variant="outline" className="border-blue-500/40 text-blue-500" data-testid="badge-late-countdown">
                        Late reg closes in {formatCountdown(lateRemainingMs!)}
                      </Badge>
                    )}
                    {tournament.status === "upcoming" ? (
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={() => cancelMutation.mutate({ id: tournamentId })}
                        disabled={cancelMutation.isPending}
                        className="w-full md:w-auto border-destructive/50 text-destructive hover:bg-destructive/10 font-bold px-8"
                        data-testid="button-cancel-rsvp"
                      >
                        {cancelMutation.isPending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <X className="mr-2 h-5 w-5" />}
                        Cancel RSVP
                      </Button>
                    ) : (
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-500" data-testid="badge-locked-in">
                        You&apos;re in — see you at the table
                      </Badge>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-end gap-2">
                    {willJoinWaitlist && (
                      <Badge variant="outline" className="border-amber-500/40 text-amber-500">
                        Tournament is full — you&apos;ll join the waitlist
                      </Badge>
                    )}
                    {inLateWindow && showLateCountdown && (
                      <Badge variant="outline" className="border-blue-500/40 text-blue-500" data-testid="badge-late-countdown-cta">
                        Late reg closes in {formatCountdown(lateRemainingMs!)}
                      </Badge>
                    )}
                    {inLateWindow && !showLateCountdown && (
                      <Badge variant="outline" className="border-blue-500/40 text-blue-500">Late registration open</Badge>
                    )}
                    <Button
                      size="lg"
                      onClick={() => registerMutation.mutate({ id: tournamentId, data: { paymentMethod: "cash" } })}
                      disabled={registerMutation.isPending || checkoutMutation.isPending}
                      className="w-full md:w-auto bg-primary text-primary-foreground font-bold px-8"
                    >
                      {registerMutation.isPending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                      {willJoinWaitlist ? "Join Waitlist" : inLateWindow ? "Late Register" : "RSVP / Register"}
                    </Button>
                    {Number(tournament.buyIn) > 0 && !willJoinWaitlist && tournament.cardPaymentsAvailable && (
                      <Button
                        size="lg"
                        variant="outline"
                        onClick={() => checkoutMutation.mutate({ id: tournamentId, data: {} })}
                        disabled={registerMutation.isPending || checkoutMutation.isPending}
                        className="w-full md:w-auto border-primary/40 text-primary hover:bg-primary/10 font-bold px-8"
                        data-testid="button-pay-with-card"
                      >
                        {checkoutMutation.isPending ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}
                        Pay {formatMoney(Number(tournament.buyIn))} with card
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-background rounded-xl border border-border p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6 border-b border-border pb-4 gap-4 flex-wrap">
                <h2 className="text-2xl font-bold">Tournament Details</h2>
                <Button
                  asChild
                  variant="outline"
                  size="sm"
                  className="border-primary/40 text-primary hover:bg-primary/10"
                  data-testid="button-add-to-calendar"
                >
                  <a
                    href={`/api/tournaments/${tournamentId}/calendar.ics`}
                    download={`tournament-${tournamentId}.ics`}
                  >
                    <CalendarPlus className="mr-2 h-4 w-4" /> Add to calendar
                  </a>
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1 flex items-center">
                      <Calendar className="mr-2 h-4 w-4 text-primary" /> Date & Time
                    </p>
                    <p className="text-lg font-medium">{formatDateTime(tournament.date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1 flex items-center">
                      <MapPin className="mr-2 h-4 w-4 text-primary" /> Venue
                    </p>
                    <p className="text-lg font-medium">
                      {tournament.venueName ? (
                        <Link href={`/venues/${tournament.venueId}`} className="hover:text-primary hover:underline">
                          {tournament.venueName}
                        </Link>
                      ) : "TBA"}
                    </p>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1 flex items-center">
                      <Trophy className="mr-2 h-4 w-4 text-primary" /> Buy-in
                    </p>
                    <p className="text-lg font-medium">{formatMoney(tournament.buyIn)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1 flex items-center">
                      <Users className="mr-2 h-4 w-4 text-primary" /> Entries
                    </p>
                    <p className="text-lg font-medium">{tournament.entries}</p>
                  </div>
                </div>
              </div>
            </div>

            {blindStructure && blindStructure.levels.length > 0 && (
              <Collapsible defaultOpen={false} className="bg-background rounded-xl border border-border">
                <CollapsibleTrigger
                  className="flex w-full items-center justify-between p-6 sm:p-8 group"
                  data-testid="trigger-blind-structure"
                >
                  <div className="text-left">
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      <Layers className="h-6 w-6 text-primary" /> Blind Structure
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      {blindStructure.name} • {blindStructure.totalLevels} levels • {blindStructure.totalDurationMinutes} min total
                    </p>
                  </div>
                  <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="px-6 sm:px-8 pb-6 sm:pb-8">
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="w-full text-sm" data-testid="table-blind-levels">
                        <thead className="bg-card/60 text-xs uppercase tracking-wider text-muted-foreground">
                          <tr>
                            <th className="text-left px-3 py-2">Level</th>
                            <th className="text-left px-3 py-2">Blinds</th>
                            <th className="text-left px-3 py-2">Duration</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {blindStructure.levels
                            .slice()
                            .sort((a, b) => a.levelNumber - b.levelNumber)
                            .map((lvl) => (
                              <tr key={lvl.id} className={lvl.isBreak ? "bg-amber-500/5" : ""}>
                                <td className="px-3 py-2 font-medium">
                                  {lvl.isBreak ? (
                                    <span className="text-amber-500">Break</span>
                                  ) : (
                                    `Level ${lvl.levelNumber}`
                                  )}
                                </td>
                                <td className="px-3 py-2">
                                  {lvl.isBreak ? <span className="text-muted-foreground">—</span> : formatBlinds(lvl)}
                                </td>
                                <td className="px-3 py-2 text-muted-foreground">{lvl.durationMinutes} min</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}

            {payoutStructure && estimatedPayouts.length > 0 && (
              <div className="bg-background rounded-xl border border-border p-6 sm:p-8">
                <div className="flex items-baseline justify-between mb-4">
                  <h2 className="text-2xl font-bold flex items-center gap-2">
                    <Trophy className="h-6 w-6 text-primary" /> Estimated Payouts
                  </h2>
                  <p className="text-xs text-muted-foreground">
                    Based on current pool {formatMoney(tournament.prizePool)}
                  </p>
                </div>
                <div className="overflow-x-auto rounded-lg border border-border">
                  <table className="w-full text-sm" data-testid="table-estimated-payouts">
                    <thead className="bg-card/60 text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left px-3 py-2">Place</th>
                        <th className="text-right px-3 py-2">Share</th>
                        <th className="text-right px-3 py-2">Estimated Prize</th>
                        <th className="text-right px-3 py-2">Points</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {estimatedPayouts.map((row) => (
                        <tr key={row.position}>
                          <td className="px-3 py-2 font-medium">
                            <span className={row.position <= 3 ? "text-primary font-bold" : ""}>#{row.position}</span>
                          </td>
                          <td className="px-3 py-2 text-right text-muted-foreground">{row.prizePercent.toFixed(1)}%</td>
                          <td className="px-3 py-2 text-right font-medium">{formatMoney(row.amount)}</td>
                          <td className="px-3 py-2 text-right text-muted-foreground">{row.points}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="mt-3 text-xs text-muted-foreground">
                  Estimates update as more players register. Final payouts may vary.
                </p>
              </div>
            )}

            <div className="bg-background rounded-xl border border-border p-6 sm:p-8">
              <h2 className="text-2xl font-bold flex items-center gap-2 mb-4">
                <Users className="h-6 w-6 text-primary" /> Registered Players
                <span className="text-sm font-normal text-muted-foreground ml-2">
                  ({activeRoster.length}{capacity ? ` / ${capacity}` : ""})
                </span>
              </h2>
              {isRosterLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : activeRoster.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4">No players registered yet — be the first.</p>
              ) : (
                <ul className="divide-y divide-border" data-testid="list-roster-active">
                  {activeRoster.map((r) => {
                    const isMe = player && r.id === player.id;
                    const isAnonymous = !r.isPublicProfile && !isMe;
                    return (
                      <li
                        key={r.id}
                        className="flex items-center justify-between gap-3 py-3"
                        data-testid={`row-roster-${r.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="h-9 w-9 rounded-full bg-secondary/20 flex items-center justify-center text-secondary font-bold border border-secondary/30 shrink-0">
                            {isAnonymous ? <EyeOff className="h-4 w-4" /> : r.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {r.name}
                              {isMe && <span className="ml-2 text-xs text-primary">(you)</span>}
                            </p>
                            {isAnonymous && (
                              <p className="text-xs text-muted-foreground">Profile hidden</p>
                            )}
                          </div>
                        </div>
                        {r.registrationStatus === "late" && (
                          <Badge variant="outline" className="border-blue-500/40 text-blue-500 text-xs">
                            Late
                          </Badge>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {anonymousActiveCount > 0 && (
                <p className="mt-3 text-xs text-muted-foreground flex items-center gap-1">
                  <EyeOff className="h-3 w-3" />
                  Some players keep their profile private. Toggle visibility from your account page.
                </p>
              )}

              {waitlistRoster.length > 0 && (
                <div className="mt-6 pt-6 border-t border-border">
                  <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
                    Waitlist
                    <span className="text-sm font-normal text-muted-foreground">({waitlistRoster.length})</span>
                  </h3>
                  <ol className="space-y-2" data-testid="list-roster-waitlist">
                    {waitlistRoster.map((r) => {
                      const isMe = player && r.id === player.id;
                      const isAnonymous = !r.isPublicProfile && !isMe;
                      return (
                        <li
                          key={r.id}
                          className="flex items-center gap-3 text-sm"
                          data-testid={`row-waitlist-${r.id}`}
                        >
                          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-amber-500/15 text-amber-500 text-xs font-bold border border-amber-500/30">
                            {r.waitlistPosition ?? "?"}
                          </span>
                          <span className="truncate">
                            {r.name}
                            {isMe && <span className="ml-2 text-xs text-primary">(you)</span>}
                            {isAnonymous && (
                              <span className="ml-2 text-xs text-muted-foreground">(hidden)</span>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-card rounded-xl border border-border p-6">
              <h3 className="font-bold text-lg mb-4">Prize Pool</h3>
              {tournament.guaranteedPrizePool > 0 && (
                <div className="mb-4 pb-4 border-b border-border/50">
                  <p className="text-sm text-muted-foreground">Guaranteed</p>
                  <p className="text-2xl font-bold text-primary">{formatMoney(tournament.guaranteedPrizePool)}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Current Pool</p>
                <p className="text-2xl font-bold text-foreground">{formatMoney(tournament.prizePool)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
